import React, { useState } from 'react';
import { Check, ThumbsUp, Minus, ThumbsDown, Send } from 'lucide-react';
import QuoteAcceptanceForm from '../QuoteAcceptanceForm';
import { quotesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { formatEUR } from './lib';

// ── Alternative-Auswahl (altes + neues System) ───────────────────────────────
export const AlternativePicker = ({ service, theme, selectedAlternatives, onSelect }) => {
  const btn = (active) => ({
    width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: theme.radius,
    fontSize: 13, transition: 'all .15s', cursor: 'pointer', marginBottom: 6,
    background: active ? theme.primary : theme.surface,
    color: active ? '#fff' : theme.textPrimary,
    border: `1px solid ${active ? theme.primary : theme.surfaceBorder}`,
  });

  if (service.alternatives && service.alternatives.length > 0) {
    return (
      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary, marginBottom: 6 }}>Wählen Sie eine Option:</p>
        {service.alternatives.map(alt => {
          const active = (selectedAlternatives[service.id] || service.selectedAlternativeId) === alt.id;
          return (
            <button key={alt.id} style={btn(active)} onClick={() => onSelect(service.id, alt.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 500 }}>{alt.title}{active ? ' ✓' : ''}</span>
                <span style={{ fontWeight: 600 }}>{formatEUR(alt.price)}</span>
              </div>
              {alt.description && <p style={{ fontSize: 12, marginTop: 2, opacity: .85 }}>{alt.description}</p>}
            </button>
          );
        })}
      </div>
    );
  }

  if (service.hasAlternative && service.alternativeName) {
    const sel = selectedAlternatives[service.id];
    const mainActive = !sel || sel === 'main';
    return (
      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: theme.textSecondary, marginBottom: 6 }}>Alternative wählen:</p>
        <button style={btn(mainActive)} onClick={() => onSelect(service.id, 'main')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontWeight: 500 }}>{service.name}{mainActive ? ' ✓' : ''}</span>
            <span style={{ fontWeight: 600 }}>{formatEUR(service.price)}</span>
          </div>
        </button>
        <button style={btn(sel === 'alternative')} onClick={() => onSelect(service.id, 'alternative')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontWeight: 500 }}>{service.alternativeName}{sel === 'alternative' ? ' ✓' : ''}</span>
            <span style={{ fontWeight: 600 }}>{formatEUR(service.alternativePrice)}</span>
          </div>
        </button>
      </div>
    );
  }
  return null;
};

// ── Zahlungsbedingungen / Konditionen / AGB ──────────────────────────────────
export const TermsBlock = ({ quote, company, theme }) => {
  const card = {
    background: theme.surface, border: `1px solid ${theme.surfaceBorder}`,
    borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary,
  };
  const hasAny = quote.paymentTerms || quote.deliveryTime || quote.warranty || company?.agb;
  if (!hasAny) return null;
  return (
    <div style={card}>
      <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>Konditionen</h3>
      <div style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }}>
        {quote.paymentTerms && <p><strong style={{ color: theme.textPrimary }}>Zahlung:</strong> {quote.paymentTerms}</p>}
        {quote.deliveryTime && <p><strong style={{ color: theme.textPrimary }}>Lieferzeit:</strong> {quote.deliveryTime}</p>}
        {quote.warranty && <p><strong style={{ color: theme.textPrimary }}>Gewährleistung:</strong> {quote.warranty}</p>}
        <p><strong style={{ color: theme.textPrimary }}>Gültig bis:</strong> {new Date(new Date(quote.createdAt).setDate(new Date(quote.createdAt).getDate() + 30)).toLocaleDateString('de-DE')}</p>
      </div>
      {company?.agb && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: theme.primary, fontWeight: 500 }}>Allgemeine Geschäftsbedingungen anzeigen</summary>
          <div style={{ fontSize: 12, color: theme.textSecondary, whiteSpace: 'pre-wrap', marginTop: 8, lineHeight: 1.6 }}>{company.agb}</div>
        </details>
      )}
    </div>
  );
};

// ── Feedback ──────────────────────────────────────────────────────────────────
export const FeedbackBlock = ({ viewHash, theme }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      setDone(true); toast.success('Vielen Dank für Ihr Feedback!');
    } catch { toast.error('Fehler beim Senden'); } finally { setBusy(false); }
  };
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary };
  if (done) return <div style={card}><p style={{ fontSize: 14, color: theme.textSecondary }}><Check className="w-4 h-4 inline mr-1" style={{ color: '#22c55e' }} />Danke für Ihr Feedback!</p></div>;
  const opt = (val, Icon, label) => (
    <button onClick={() => setRating(val)} style={{
      flex: 1, padding: '10px', borderRadius: theme.radius, cursor: 'pointer',
      background: rating === val ? theme.primary : 'transparent',
      color: rating === val ? '#fff' : theme.textSecondary,
      border: `1px solid ${rating === val ? theme.primary : theme.surfaceBorder}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontSize: 12,
    }}><Icon className="w-5 h-5" />{label}</button>
  );
  return (
    <div style={card}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Wie gefällt Ihnen dieses Angebot?</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {opt('positive', ThumbsUp, 'Gut')}
        {opt('neutral', Minus, 'Neutral')}
        {opt('negative', ThumbsDown, 'Unklar')}
      </div>
      {rating && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Kommentar (optional)"
            style={{ flex: 1, padding: '8px 12px', borderRadius: theme.radius, border: `1px solid ${theme.surfaceBorder}`, background: theme.dark ? '#0f172a' : '#fff', color: theme.textPrimary, fontSize: 13 }} />
          <button onClick={submit} disabled={busy} style={{ padding: '8px 16px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>Senden</button>
        </div>
      )}
    </div>
  );
};

// ── Kundenfrage ───────────────────────────────────────────────────────────────
export const QuestionBlock = ({ viewHash, theme }) => {
  const [form, setForm] = useState({ name: '', email: '', question: '' });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.question) { toast.error('Bitte Name und Frage ausfüllen'); return; }
    setBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/view/${viewHash}/question`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      setDone(true); toast.success('Ihre Frage wurde übermittelt!');
    } catch { toast.error('Fehler beim Senden'); } finally { setBusy(false); }
  };
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '20px 24px', color: theme.textPrimary };
  const inp = { width: '100%', padding: '9px 12px', borderRadius: theme.radius, border: `1px solid ${theme.surfaceBorder}`, background: theme.dark ? '#0f172a' : '#fff', color: theme.textPrimary, fontSize: 13, marginBottom: 8 };
  if (done) return <div style={card}><p style={{ fontSize: 14, color: theme.textSecondary }}><Check className="w-4 h-4 inline mr-1" style={{ color: '#22c55e' }} />Ihre Frage wurde gesendet.</p></div>;
  return (
    <div style={card}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Haben Sie eine Frage?</h3>
      <form onSubmit={submit}>
        <input style={inp} placeholder="Ihr Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <input style={inp} placeholder="Ihre E-Mail (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <textarea style={{ ...inp, minHeight: 70 }} placeholder="Ihre Frage…" value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} />
        <button type="submit" disabled={busy} style={{ padding: '9px 18px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Send className="w-4 h-4" />Frage senden
        </button>
      </form>
    </div>
  );
};

// ── Annahme-CTA / Annahme-Status / Annahme-Formular ──────────────────────────
export const AcceptanceBlock = ({ ctx, theme }) => {
  const { quote, viewHash, quoteAccepted, setQuoteAccepted, showAcceptanceForm, setShowAcceptanceForm,
    optionalServicesSelected, handleSaveAsPdf, refreshQuote } = ctx;
  const companyColors = { primary: theme.primary, einmalig: theme.einmalig, monatlich: theme.monatlich, jaehrlich: theme.jaehrlich };

  if (quoteAccepted) {
    return (
      <div style={{ background: theme.dark ? '#0b2e22' : '#ecfdf5', border: `1px solid ${theme.dark ? '#065f46' : '#a7f3d0'}`, borderRadius: theme.radius, padding: '28px 24px', textAlign: 'center', color: theme.textPrimary }}>
        <Check className="w-14 h-14 mx-auto mb-3" style={{ color: '#10b981' }} />
        <h3 style={{ fontSize: 22, fontWeight: 700, color: theme.dark ? '#6ee7b7' : '#065f46', marginBottom: 6 }}>Angebot angenommen</h3>
        <p style={{ fontSize: 14, color: theme.textSecondary }}>Angenommen am {new Date(quote.acceptedAt).toLocaleString('de-DE')}.</p>
        <button onClick={handleSaveAsPdf} style={{ marginTop: 18, padding: '11px 22px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14 }}>
          Rechtsverbindliches PDF herunterladen
        </button>
      </div>
    );
  }

  if (showAcceptanceForm) {
    return (
      <QuoteAcceptanceForm
        viewHash={viewHash}
        optionalServicesSelected={optionalServicesSelected}
        companyColors={companyColors}
        onDownloadPdf={handleSaveAsPdf}
        onAcceptanceComplete={() => { setQuoteAccepted(true); setShowAcceptanceForm(false); refreshQuote(); }}
      />
    );
  }

  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '32px 24px', textAlign: 'center', color: theme.textPrimary }}>
      <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Bereit, loszulegen?</h3>
      <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 20 }}>Nehmen Sie das Angebot digital an — mit oder ohne Unterschrift.</p>
      <button onClick={() => setShowAcceptanceForm(true)} style={{ padding: '14px 32px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Check className="w-5 h-5" />Angebot jetzt annehmen
      </button>
    </div>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────────
export const FooterBlock = ({ company, theme }) => (
  <div style={{ textAlign: 'center', fontSize: 12, color: theme.textMuted, paddingTop: 8 }}>
    <p style={{ fontWeight: 500, color: theme.textSecondary }}>{company?.name}</p>
    <p>{company?.address} • {company?.city}</p>
    <p>Tel: {company?.phone} • {company?.email}{company?.website ? ` • ${company.website}` : ''}</p>
  </div>
);
